import { useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'

// UI
import { Flex, SimpleGrid, VStack } from '@chakra-ui/react'

//API

import { DatePicker, DummyInput, Input, Textarea, VersionFileInput } from '@/components/Form/'
import { numberWithCommas } from '@/utils/stringHelper'

//Constant
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { useCustomer } from '@/features/customers/useCustomer'
import { OfferDocumentTypes } from '@/features/offers/constants'
import { offerQuotationGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerQuotationEditRights'
import { selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'

const LegalForm = () => {
  // Redux state
  const offerPermissions = useSelector(selectOfferPermissions)

  // Hooks
  const { useFetchAllCustomers } = useCustomer()

  const { data: customerResponse } = useFetchAllCustomers({ page: 1, pageSize: 0, filters: { status: 'active' } })

  const contributors = useWatch({ name: 'contributors' }) ?? []
  const totalLiabilityCap = useWatch({ name: 'commercialChecklist.totalLiabilityCap' })
  const customerId = useWatch({ name: 'customer' })
  const endCustomerId = useWatch({ name: 'endCustomer' })
  const subSystems = useWatch({ name: 'subSystems' }) ?? []

  //Translated
  /**
   * Legal User is responsible to fill conclusion and upload legal document
   * Technical User is responsible to fill rest of the field
   */
  const canEditLegalDocument = offerPermissions[offerQuotationGroupPolicyType.QUOTATION_DOCUMENTS_LEGAL]?.edit ?? false
  const canEditLegal = offerPermissions[offerQuotationGroupPolicyType.QUOTATION_LEGAL]?.edit ?? false
  const canEditLegalConclusion =
    offerPermissions[offerQuotationGroupPolicyType.QUOTATION_LEGAL_CONCLUSION]?.edit ?? false

  const legalContributorName =
    contributors.find((contributor) => contributor.group === ContributorGroupType.LEGAL)?.user?.fullName || ''
  const customerName = Array.isArray(customerResponse?.data)
    ? customerResponse.data?.find((c) => c?._id === customerId)?.name
    : '(-)'
  const endCustomerName = Array.isArray(customerResponse?.data)
    ? customerResponse.data?.find((c) => c?._id === endCustomerId)?.name
    : '(-)'

  const totalSubsystemPrice = (() => {
    if (!subSystems) {
      return 0
    }
    return subSystems.reduce((systemPrice, subSystem) => {
      const subSystemTotal = subSystem.totalContractPrice?.newContractRows.reduce((sum, row) => {
        return sum + row.totalHKD
      }, 0)

      return systemPrice + subSystemTotal
    }, 0)
  })()

  const liabilityCapText = (() => {
    if (!totalLiabilityCap?.value) {
      return ''
    }
    const percentage = parseFloat(totalLiabilityCap?.other || 0) || 0
    const amount = (totalSubsystemPrice * percentage) / 100
    switch (totalLiabilityCap?.value) {
      case 'limited':
        return `Limited at $${numberWithCommas(totalLiabilityCap?.other || 0)}`

      case 'limitedPercentage':
        return `Limited at $${numberWithCommas(amount)} (${percentage}%)`

      case 'unlimited':
        return 'Unlimited'

      default:
        return totalLiabilityCap?.value
    }
  })()

  return (
    <>
      <SimpleGrid columns={[2, null, 2]} spacingX="40px" spacingY="15px" mb="5">
        <DummyInput label="Name Of Legal Council" value={legalContributorName} isRow />
        <DummyInput label="Customer" value={customerName} isRow />
        <DummyInput label="End Customer" value={endCustomerName} isRow />
        <DummyInput label="Liability Cap" value={liabilityCapText} isRow />
        <DatePicker name="offerSubmissionDate" label="Offer Submission Date" disabled isRow />
      </SimpleGrid>

      <VStack mb="5">
        <VersionFileInput
          label="Commercial Deviation List"
          name="documents.externalCommercialDeviation"
          fileType={OfferDocumentTypes.DOCUMENTS_EXTERNAL_COMMERCIAL_DEVIATION}
          canUpload={canEditLegalDocument}
          isRow
          isRequired={false}
        />
        <VersionFileInput
          label="Legal Statement"
          name="documents.internalLegalStatement"
          fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_LEGAL_STATEMENT}
          canUpload={canEditLegalDocument}
          isMultiple
          isRow
          isRequired={false}
        />
        <VersionFileInput
          label="Supporting and Correspondence"
          name="documents.internalLegalComments"
          fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_LEGAL_COMMENTS}
          canUpload={canEditLegalDocument}
          isMultiple
          isRow
          isRequired={false}
        />
        <VersionFileInput
          label="IPR Checklist"
          name="documents.internalIPRChecklist"
          fileType={OfferDocumentTypes.DOCUMENTS_INTERNAL_IPR_CHECKLIST}
          canUpload={canEditLegalDocument}
          isMultiple
          isRow
          isRequired={false}
        />
      </VStack>
      <Flex mb="5">
        <Textarea
          name="legal.conclusion"
          label="Legal's comments"
          placeholder=""
          disabled={!canEditLegalConclusion}
          rows="3"
        />
      </Flex>
      <SimpleGrid columns={[2, null, 2]} spacingX="40px" spacingY="15px">
        <Input
          name="legal.projectNature"
          label="Project Nature"
          type="text"
          placeholder=""
          isRequired={true}
          isRow
          disabled={!canEditLegal}
        />

        <DatePicker
          name="legal.projectCompletionDate"
          label="Project Completion or Contract Expiry Date"
          disabled={!canEditLegal}
          isRow
        />

        <Input
          name="legal.contactFormPreviouslyReviewed"
          label="Contract Form Previously Reviewed? (Specify project, date)"
          type="text"
          placeholder=""
          isRequired={true}
          isRow
          disabled={!canEditLegal}
        />
        <Input
          name="legal.projectIPLicense"
          label="Project IP License/Transfer (Reason for transfer)"
          type="text"
          placeholder=""
          isRequired={true}
          isRow
          disabled={!canEditLegal}
        />
        <Input
          name="legal.LoAProcessLevelRequired"
          label="LoA Process Level Required? (Specify deadline)"
          type="text"
          placeholder=""
          isRequired={true}
          isRow
          disabled={!canEditLegal}
        />
      </SimpleGrid>
    </>
  )
}

export default LegalForm
